import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Angle,
  Element,
  ElementType,
  Point,
  Rect,
  Text,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import { getCornerCoords } from './Elements';
import {
  createAnglePath,
  createRect,
  createTrapezoidPath,
  createTrianglePath,
  SHARP_CORNER_STYLE,
} from './shapes';
import { COLOR_GHOST_LINE, COLOR_HANDLE } from '../../constants';
import { screenPx, worldPx } from '../../helpers/handleScale';
import {
  MIN_RADIUS_HANDLE_OFFSET_PX,
  RADIUS_HANDLE_HOVER_RADIUS_PX,
  RADIUS_LABEL_FONT_SIZE_PX,
  RADIUS_LABEL_OFFSET_PX,
  RADIUS_RAIL_TIC_HALF_LENGTH_PX,
  SPLIT_HANDLE_SIZE_PX,
  SPLIT_HIT_STROKE_PX,
  SPLIT_ICON_ARM_RATIO,
  SPLIT_STROKE_WIDTH_PX,
} from '../../helpers/handleTokens';
import {
  getCappedHandleScale,
  getCornerBisector,
  getHandleButtonPosition,
  getPerpendicularGuides,
  getTriApexHandlePosition,
  makeRadiusCursorUrl,
  makeResizeCursorUrl,
} from '../../helpers/utility';
import { useMeasurement } from '../../hooks/useMeasurement';
import {
  HandleHitArea,
  HandleHitLine,
  RoundHandle,
  SquareHandle,
} from './handles/Handle';

export const RenderCornerSelectors = (
  e: Element,
  scale: number,
  showRotationHandler = true,
  showResizeHandler = true,
  showTextEditHandler = false
) => {
  const cappedScale = getCappedHandleScale(scale);

  const rotate = e.rotate;
  const tlBrCursor = makeResizeCursorUrl(45 + rotate);
  const trBlCursor = makeResizeCursorUrl(135 + rotate);

  const { tL, tR, bR, bL } = getCornerCoords(e);
  const corners = [
    { name: 'top-left', at: tL, cursor: tlBrCursor },
    { name: 'top-right', at: tR, cursor: trBlCursor },
    { name: 'bottom-right', at: bR, cursor: tlBrCursor },
    { name: 'bottom-left', at: bL, cursor: trBlCursor },
  ];

  const { handleY, handleSize, rotateX, editTextX } = getHandleButtonPosition(
    cappedScale,
    tL,
    tR,
    showTextEditHandler
  );

  return (
    <>
      {showRotationHandler && (
        <svg
          id={`${e.id}-rotate`}
          className="rotating"
          x={rotateX}
          y={handleY}
          width={handleSize}
          height={handleSize}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            id={`${e.id}-rotate`}
            cx="12"
            cy="12"
            r="12"
            fill="var(--active-blue)"
          />
          <g id={`${e.id}-rotate`} clipPath="url(#clip0_12_203)">
            <path
              d="M6 12.4405C6 15.7053 8.62681 18.345 11.8837 18.345C15.1373 18.345 17.7609 15.7053 17.7609 12.4405C17.7609 9.17247 15.1826 6.5749 11.9354 6.5749C11.7027 6.5749 11.4798 6.59757 11.2795 6.6332L12.5654 5.3668C12.7076 5.22105 12.7916 5.05263 12.7916 4.82591C12.7916 4.36599 12.4362 4 11.9903 4C11.7609 4 11.5735 4.07773 11.4249 4.23644L8.78837 6.92793C8.61712 7.09312 8.52988 7.31012 8.52988 7.53037C8.52988 7.76356 8.61389 7.96437 8.78837 8.13603L11.4249 10.8145C11.5735 10.9635 11.7609 11.0381 11.9871 11.0381C12.4362 11.0381 12.7916 10.685 12.7916 10.2283C12.7916 10.0016 12.7076 9.8332 12.5622 9.68745L11.1115 8.24615C11.3506 8.20081 11.6317 8.17814 11.9354 8.17814C14.2649 8.17814 16.0937 10.0502 16.0937 12.4405C16.0937 14.7822 14.2197 16.6736 11.8837 16.6736C9.54442 16.6736 7.66074 14.7822 7.66074 12.4405C7.66074 11.9352 7.32795 11.5854 6.84329 11.5854C6.35218 11.5854 6 11.9352 6 12.4405Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_12_203">
              <rect
                width="12"
                height="16"
                fill="white"
                transform="translate(6 4)"
              />
            </clipPath>
          </defs>
        </svg>
      )}
      {showTextEditHandler && (
        <svg
          id={`${e.id}-edit-text`}
          x={editTextX}
          y={handleY}
          width={handleSize}
          height={handleSize}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ cursor: 'pointer' }}
        >
          <circle cx="12" cy="12" r="12" fill="var(--active-blue)" />
          <path
            transform="translate(4 4) scale(0.667)"
            fill="white"
            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
          />
        </svg>
      )}
      {showResizeHandler && (
        <>
          {corners.map(({ name, at, cursor }) => (
            <SquareHandle
              key={name}
              id={`${e.id}-resize-${name}`}
              data-testid={
                name === 'bottom-right' ? 'resize-bottom-right' : undefined
              }
              cx={at.x}
              cy={at.y}
              scale={scale}
              cursor={cursor}
            />
          ))}
        </>
      )}
    </>
  );
};

export const RenderApexHandle = (e: Triangle | Angle, scale: number) => {
  if (e.type !== ElementType.Triangle && e.type !== ElementType.Angle) {
    return null;
  }
  const cappedScale = getCappedHandleScale(scale);

  const { apexX, apexY } = getTriApexHandlePosition(e);

  return (
    <RoundHandle
      id={`${e.id}-apex`}
      cx={apexX}
      cy={apexY + 15 * cappedScale}
      scale={scale}
      cursor={makeResizeCursorUrl(e.rotate)}
    />
  );
};

export const RenderRectOutline = ({
  id,
  x,
  y,
  width,
  height,
  scale,
}: {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}) => {
  const scaledStrokeWidth = worldPx(3, scale);
  return (
    <path
      key={`outline-${id}`}
      id={id}
      style={{
        stroke: COLOR_HANDLE,
        fill: 'none',
        opacity: 1,
        color: '#000',
        strokeWidth: scaledStrokeWidth,
      }}
      d={createRect({
        x: x,
        y: y,
        width: width,
        height: height,
        thickness: 1,
      })}
      className="isSelected"
      data-testid="rect-svg"
    />
  );
};

export const RenderShapeOutline = ({
  element,
  scale,
}: {
  element: Rect | Text | Triangle | Trapezoid | Angle;
  scale: number;
}) => {
  const scaledStrokeWidth = worldPx(3, scale);
  const baseStyle: React.CSSProperties = {
    stroke: COLOR_HANDLE,
    fill: 'none',
    opacity: 1,
    color: '#000',
    strokeWidth: scaledStrokeWidth,
  };
  const { x, y, width, height } = element;

  let d: string;
  let style = baseStyle;
  if (element.type === ElementType.Triangle) {
    d = createTrianglePath(element);
    style = { ...baseStyle, ...SHARP_CORNER_STYLE };
  } else if (element.type === ElementType.Trapezoid) {
    d = createTrapezoidPath(element);
    style = { ...baseStyle, ...SHARP_CORNER_STYLE };
  } else if (element.type === ElementType.Angle) {
    d = createAnglePath(element);
    style = { ...baseStyle, ...SHARP_CORNER_STYLE };
  } else {
    d = createRect({ x, y, width, height, thickness: 1 });
  }

  return (
    <path
      key={`outline-${element.id}`}
      id={element.id}
      style={style}
      d={d}
      className="isSelected"
      data-testid="shape-outline"
    />
  );
};

export const RenderLineOutline = ({
  id,
  pathData,
  scale,
}: {
  id: string;
  pathData: string;
  scale: number;
}) => {
  const scaledStrokeWidth = worldPx(3, scale);

  return (
    <path
      key={`outline-${id}`}
      id={id}
      d={pathData}
      fill="transparent"
      markerStart={`url(#${id}-arrow-reverse)`}
      markerEnd={`url(#${id}-arrow)`}
      data-testid="polyline"
      stroke={COLOR_HANDLE}
      className="isSelected"
      strokeWidth={scaledStrokeWidth}
    ></path>
  );
};

export const RenderGhostLine = ({
  id,
  pathData,
  scale,
}: {
  id: string;
  pathData: string;
  scale: number;
}) => {
  return (
    <path
      key={`ghost-${id}`}
      d={pathData}
      fill="transparent"
      stroke={COLOR_GHOST_LINE}
      strokeWidth={worldPx(1.5, scale)}
      data-testid="polyline-ghost-line"
      style={{ pointerEvents: 'none' }}
    ></path>
  );
};

export const RenderRadiusHandles = ({
  points,
  processedPoints,
  id,
  scale,
  scalingFactor,
  hoveredCorner,
  setHoveredCorner,
  isRadiusDragging,
}: {
  points: Point[];
  processedPoints: [number, number, boolean][];
  id: string;
  scale: number;
  scalingFactor: number;
  hoveredCorner: number | null;
  setHoveredCorner: (index: number | null) => void;
  isRadiusDragging: boolean;
}) => {
  const intl = useIntl();
  const { formatLength } = useMeasurement();
  const [hoveredHandle, setHoveredHandle] = useState<number | null>(null);
  // minOffset is a constraint on the drawing itself (it is compared against
  // cornerRadius, and mirrored in useRadiusDragging), so it stays uncapped. The rest is
  // UI and holds a constant size on screen.
  const minOffset = worldPx(MIN_RADIUS_HANDLE_OFFSET_PX, scale);
  const hoverRadius = worldPx(RADIUS_HANDLE_HOVER_RADIUS_PX, scale);
  const labelOffset = screenPx(RADIUS_LABEL_OFFSET_PX, scale);
  const labelFontSize = screenPx(RADIUS_LABEL_FONT_SIZE_PX, scale);
  const isClosed = processedPoints.some(([, , connected]) => connected);

  return processedPoints.map(([cx, cy], i) => {
    if (i === processedPoints.length - 1) return null;
    if (i === 0 && !isClosed) return null;
    if (points[i]?.connectingPointIndex !== undefined) return null;

    const [px, py] =
      i === 0
        ? processedPoints[processedPoints.length - 2]
        : processedPoints[i - 1];
    const [nx, ny] = processedPoints[i + 1];

    const bisector = getCornerBisector(
      { x: px, y: py },
      { x: cx, y: cy },
      { x: nx, y: ny }
    );
    if (!bisector) return null;
    const { bisDirX, bisDirY, maxRadius } = bisector;

    const currentRadius = points[i]?.cornerRadius ?? 0;
    const handleDist = Math.min(Math.max(minOffset, currentRadius), maxRadius);
    const handlePos = {
      x: cx + bisDirX * handleDist,
      y: cy + bisDirY * handleDist,
    };

    const railStart = {
      x: cx + bisDirX * minOffset,
      y: cy + bisDirY * minOffset,
    };
    const railEnd = {
      x: cx + bisDirX * maxRadius,
      y: cy + bisDirY * maxRadius,
    };
    const ticHalfLength = worldPx(RADIUS_RAIL_TIC_HALF_LENGTH_PX, scale);
    const perpX = -bisDirY;
    const perpY = bisDirX;
    const showRail = maxRadius > minOffset;
    const isHovered = hoveredCorner === i;
    const showLabel = hoveredHandle === i || (isRadiusDragging && isHovered);

    return (
      <g
        key={`${id}-radius-polyline-${i}`}
        onMouseEnter={() => !isRadiusDragging && setHoveredCorner(i)}
        onMouseLeave={() => {
          if (isRadiusDragging) return;
          setHoveredCorner(null);
          setHoveredHandle(null);
        }}
      >
        <g data-testid={`${id}-radius-hover-${i}`}>
          {/* Both carry the bare element id, not the handle id, so a click on the
              wide hit area falls through to selecting the element. */}
          <HandleHitLine
            id={id}
            x1={cx}
            y1={cy}
            x2={railEnd.x}
            y2={railEnd.y}
            strokeWidth={hoverRadius * 2}
          />
          <HandleHitArea id={id} cx={cx} cy={cy} radius={hoverRadius} />
        </g>
        {isHovered && showRail && (
          <g
            data-testid={`${id}-radius-rail-${i}`}
            stroke={COLOR_GHOST_LINE}
            strokeWidth={worldPx(1.5, scale)}
            style={{ pointerEvents: 'none' }}
          >
            <line
              x1={railStart.x}
              y1={railStart.y}
              x2={railEnd.x}
              y2={railEnd.y}
            />
            <line
              x1={railStart.x - perpX * ticHalfLength}
              y1={railStart.y - perpY * ticHalfLength}
              x2={railStart.x + perpX * ticHalfLength}
              y2={railStart.y + perpY * ticHalfLength}
            />
            <line
              x1={railEnd.x - perpX * ticHalfLength}
              y1={railEnd.y - perpY * ticHalfLength}
              x2={railEnd.x + perpX * ticHalfLength}
              y2={railEnd.y + perpY * ticHalfLength}
            />
          </g>
        )}
        {isHovered && (
          <RoundHandle
            id={`${id}-radius-polyline-${i}`}
            cx={handlePos.x}
            cy={handlePos.y}
            scale={scale}
            cursor={makeRadiusCursorUrl()}
            onMouseEnter={() => setHoveredHandle(i)}
            onMouseLeave={() => setHoveredHandle(null)}
          />
        )}
        {showLabel && (
          <text
            x={handlePos.x + labelOffset}
            y={handlePos.y - labelOffset}
            fontSize={labelFontSize}
            className="radius-label"
            style={{ pointerEvents: 'none' }}
            data-testid={`${id}-radius-label-${i}`}
          >
            {intl.formatMessage(
              { id: 'resizeHandlers.radiusLabel' },
              {
                radius: formatLength(
                  Math.min(currentRadius, maxRadius) * scalingFactor
                ),
              }
            )}
          </text>
        )}
      </g>
    );
  });
};

export const RenderSplitHandles = ({
  processedPoints,
  scale,
  id,
  hoveredSegment,
  setHoveredSegment,
}: {
  processedPoints: [number, number, boolean][];
  scale: number;
  id: string;
  hoveredSegment: number | null;
  setHoveredSegment: (index: number | null) => void;
}) => {
  const hitAreaStrokeWidth = screenPx(SPLIT_HIT_STROKE_PX, scale);
  const iconArm =
    screenPx(SPLIT_HANDLE_SIZE_PX / 2, scale) * SPLIT_ICON_ARM_RATIO;
  const iconStyle = {
    pointerEvents: 'none',
    ['--handle-stroke-width' as string]: screenPx(SPLIT_STROKE_WIDTH_PX, scale),
  } as React.CSSProperties;

  return (
    <>
      {processedPoints.map(([x0, y0], i) => {
        if (i === processedPoints.length - 1) return null;
        const [x1, y1] = processedPoints[i + 1];
        const mx = (x0 + x1) / 2;
        const my = (y0 + y1) / 2;
        const isActive = hoveredSegment === i;

        return (
          <g
            key={`${id}-split-polyline-${i}`}
            onMouseEnter={() => setHoveredSegment(i)}
            onMouseLeave={() => setHoveredSegment(null)}
          >
            {/* Carries the bare element id so the wide hit area still selects. */}
            <HandleHitLine
              id={id}
              x1={x0}
              y1={y0}
              x2={x1}
              y2={y1}
              strokeWidth={hitAreaStrokeWidth}
            />
            {isActive && (
              <g className="split-handle" style={{ cursor: 'pointer' }}>
                <SquareHandle
                  id={`${id}-split-polyline-${i}`}
                  cx={mx}
                  cy={my}
                  scale={scale}
                  sizePx={SPLIT_HANDLE_SIZE_PX}
                  strokeWidthPx={SPLIT_STROKE_WIDTH_PX}
                />
                <line
                  className="handle-icon"
                  x1={mx - iconArm}
                  y1={my}
                  x2={mx + iconArm}
                  y2={my}
                  strokeLinecap="round"
                  style={iconStyle}
                />
                <line
                  className="handle-icon"
                  x1={mx}
                  y1={my - iconArm}
                  x2={mx}
                  y2={my + iconArm}
                  strokeLinecap="round"
                  style={iconStyle}
                />
              </g>
            )}
          </g>
        );
      })}
    </>
  );
};

export const RenderLineResizeHandles = ({
  processedPoints,
  scale,
  id,
}: {
  processedPoints: [number, number, boolean][];
  scale: number;
  id: string;
}) => {
  return processedPoints.map(([x, y], i) => (
    <SquareHandle
      key={`${id}-resize-polyline-${i}`}
      id={`${id}-resize-polyline-${i}`}
      cx={x}
      cy={y}
      scale={scale}
    />
  ));
};

export const RenderPerpendicularGuide = ({
  id,
  processedPoints,
  scale,
}: {
  id: string;
  processedPoints: [number, number, boolean][];
  scale: number;
}) => {
  const guides = getPerpendicularGuides(processedPoints);
  if (!guides) return null;

  return (
    <>
      {guides.map((guide, i) => (
        <line
          key={`${id}-perpendicular-guide-${i}`}
          data-testid={`perpendicular-guide-${i}`}
          x1={guide.x1}
          y1={guide.y1}
          x2={guide.x2}
          y2={guide.y2}
          stroke={COLOR_HANDLE}
          strokeWidth={worldPx(1.4, scale)}
          strokeDasharray="8 3"
          pointerEvents="none"
        />
      ))}
    </>
  );
};
